import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Copy,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { FaMicrosoft } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";

// Local implementation of fetchFieldSyncLogs using the centralized API helper
import { queryCustomEvents, apiRequest, fetchFieldMappings } from "../lib/api";

async function fetchFieldSyncLogs(
  parentEntity?: string,
  status?: string,
  batchId?: string,
  startTime?: string,
  endTime?: string,
  lastSeenTimestamp?: string,
  lastSeenId?: string,
  searchTerm?: string,
  limit: number = 100
) {
  // Build query parameters for the field mapping logs endpoint
  const params: any = {
    limit,
    includeStatusCounts: true,
  };
  if (parentEntity && parentEntity !== "all") params.parentEntity = parentEntity;
  if (status && status !== "all") params.status = status;
  if (batchId && batchId !== "all") params.batchId = batchId;
  if (startTime) params.startTime = startTime;
  if (endTime) params.endTime = endTime;
  if (lastSeenTimestamp) params.lastSeenTimestamp = lastSeenTimestamp;
  if (lastSeenId) params.lastSeenId = lastSeenId;
  if (searchTerm) params.searchTerm = searchTerm;
  // Use the field mapping logs endpoint (isEntityMapping = false)
  return await queryCustomEvents(params, false);
}

import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  Row,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "wouter";
import { format } from "date-fns";
import type { FieldMappingSyncLog, SyncLogsResponse } from "../../shared/api-types";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { ViewGuard } from "@/components/auth/PermissionGuards";

// interface FieldSyncLogsApiResponse {
//   logs: FieldMappingSyncLog[];
//   statusCounts?: {
//     done: number;
//     error: number;
//     no_update_needed: number;
//     pending: number;
//     total: number;
//   };
// }

// Define the complete list of available columns including all possible FieldMappingSyncLog properties
const ALL_COLUMNS = [
  { key: "entityName", label: "Entity Name", priority: 1 },
  { key: "entityId", label: "Entity ID", priority: 1 },
  { key: "status", label: "Status", priority: 1 },
  { key: "timestamp", label: "Timestamp", priority: 1 },
  { key: "batchId", label: "Batch ID", priority: 1 },
  { key: "updatedFieldsCount", label: "# Updated", priority: 1 },
  { key: "updatedFields", label: "Updated Fields", priority: 3 },
  { key: "processedAt", label: "Processed At", priority: 3 },
  { key: "mappingId", label: "Mapping ID", priority: 3 },
  { key: "errorMessage", label: "Error Message", priority: 3 },
  { key: "correlationId", label: "Correlation ID", priority: 4 },
  { key: "relationName", label: "Relation Name", priority: 4 },
  { key: "message", label: "Message", priority: 3 },
  { key: "error", label: "Error", priority: 3 },
  { key: "syncLogs", label: "Logs", priority: 3 },
];

interface FieldSyncLogsProps {
  onRefreshReady?: (refreshFn: () => void, isRefreshing: boolean) => void;
}

const FieldSyncLogs = ({ onRefreshReady }: FieldSyncLogsProps = {}) => {
  const [parentEntity, setParentEntity] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<{
    mode: 'include' | 'exclude';
    statuses: string[];
  }>({ mode: 'include', statuses: [] });
  const [batchId, setBatchId] = useState<string>("all");
  const [timePeriod, setTimePeriod] = useState<string>("all");
  const [limit, setLimit] = useState<number>(25);
  // Remove offset-based pageIndex, add cursor stack for previous/next
  const [cursorStack, setCursorStack] = useState<Array<{ lastSeenTimestamp: string | null, lastSeenId: string | null }>>([{ lastSeenTimestamp: null, lastSeenId: null }]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);
  const [selectedLog, setSelectedLog] = useState<FieldMappingSyncLog | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  // State for server-side search term
  const [searchTerm, setSearchTerm] = useState<string>("");

  // For table sorting
  const [sorting, setSorting] = useState<SortingState>([
    { id: "timestamp", desc: true },
  ]);

  // For table filtering
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Query to get available entity names for dropdown options (from FieldMappings config)
  const { data: availableEntities, isLoading: entitiesLoading } = useQuery<string[]>({
    queryKey: ["/api/field-mappings", "entities"],
    queryFn: async () => {
      const mappings = await fetchFieldMappings();
      const unique = Array.from(
        new Set((mappings || []).map(m => m.entityName).filter(Boolean))
      );
      unique.sort((a, b) => a.localeCompare(b));
      return unique;
    },
    staleTime: 300000, // 5 minutes
  });

  // Compute current cursor
  const currentCursor = cursorStack[currentCursorIndex] || { lastSeenTimestamp: null, lastSeenId: null };

  // Query to get sync logs based on selected filters and cursor-based pagination
  const {
    data: syncLogsData,
    isLoading: syncLogsLoading,
    isRefetching,
    refetch,
  } = useQuery<SyncLogsResponse>({
    queryKey: [
      "/api/query-field-mapping-logs",
      { parentEntity, statusFilter, batchId, timePeriod, searchTerm, limit, lastSeenTimestamp: currentCursor.lastSeenTimestamp, lastSeenId: currentCursor.lastSeenId },
    ],
    queryFn: async () => {
      // Calculate start time based on time period
      const startTime = timePeriod !== "all" ? getStartTimeFromPeriod(timePeriod) : undefined;

      // Add current time as endTime when startTime is defined
      const endTime = startTime ? new Date().toISOString() : undefined;

      // Log query parameters for debugging
      console.log("Filter values:", {
        parentEntity,
        statusFilter,
        batchId,
        timePeriod,
        startTime,
        endTime,
        limit,
        cursor: currentCursor
      });

      // Call fetchFieldSyncLogs without status filter (we'll filter client-side)
      // Fetch more records to account for client-side filtering
      const response = await fetchFieldSyncLogs(
        parentEntity !== "all" ? parentEntity : undefined,
        undefined, // Don't filter by status on server
        batchId !== "all" ? batchId : undefined,
        startTime,
        endTime, // endTime (current time)
        currentCursor.lastSeenTimestamp || undefined,
        currentCursor.lastSeenId || undefined,
        searchTerm || undefined,
        limit * 3 // Fetch more to account for filtering
      ) as SyncLogsResponse;

      // Apply client-side status filtering
      let filteredLogs = response.logs;
      if (statusFilter.statuses.length > 0) {
        filteredLogs = response.logs.filter(log => {
          const matchesStatus = statusFilter.statuses.includes(log.status || '');
          return statusFilter.mode === 'include' ? matchesStatus : !matchesStatus;
        });
      }

      // Limit to requested amount after filtering
      filteredLogs = filteredLogs.slice(0, limit);

      // Log the response for debugging
      console.log("Field sync logs response:", {
        logCount: response.logs?.length,
        filteredLogCount: filteredLogs.length,
        totalCount: response.totalCount,
        statusCounts: response.statusCounts
      });

      return {
        ...response,
        logs: filteredLogs
      };
    },
    staleTime: 10000,
  });

  // Invalidate and refetch when filters change
  useEffect(() => {
    refetch();
  }, [statusFilter, parentEntity, batchId, timePeriod, searchTerm]);

  const uniqueParentEntities = availableEntities || [];

  // Fetch batch IDs with counts using optimized endpoint
  const { data: batchIdsData } = useQuery<{
    batchIds: Array<{ batchId: string; recordCount: number }>;
    metadata: { count: number; totalRecords: number; queryDurationMs: number };
  }>({
    queryKey: ["/api/getBatchIds", parentEntity, timePeriod, "field"],
    queryFn: async () => {
      const params = new URLSearchParams({
        isEntityMapping: "false",
      });
      if (parentEntity && parentEntity !== "all") params.set("parentEntity", parentEntity);

      // Add time range to batch ID query
      const startTime = getStartTimeFromPeriod(timePeriod);
      if (startTime) {
        params.set("startTime", startTime);
        params.set("endTime", new Date().toISOString());
      }

      const response = await apiRequest('GET', `/api/getBatchIds?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch batch IDs");
      return response.json();
    },
    staleTime: 60000, // Cache for 1 minute since batch IDs don't change often
  });

  // Get batch IDs with counts from the optimized query
  const batchIdsWithCounts = useMemo(() => {
    return batchIdsData?.batchIds || [];
  }, [batchIdsData]);

  // Helper function to convert time period to date string
  const getStartTimeFromPeriod = (period: string): string | undefined => {
    if (period === "all") return undefined;
    
    // Make sure we have a valid period format
    if (!(period.endsWith('h') || period.endsWith('d'))) {
      console.error(`Invalid time period format: ${period}. Expected format like "24h" or "7d"`);
      return undefined;
    }
    
    try {
      const now = new Date();
      const startDate = new Date(now);
      
      // Check if the period is in hours or days
      if (period.endsWith('h')) {
        const hours = parseInt(period.replace('h', ''));
        if (isNaN(hours)) {
          console.error(`Invalid hour value: ${period}`);
          return undefined;
        }
        startDate.setHours(now.getHours() - hours);
      } else if (period.endsWith('d')) {
        const days = parseInt(period.replace('d', ''));
        if (isNaN(days)) {
          console.error(`Invalid day value: ${period}`);
          return undefined;
        }
        startDate.setDate(now.getDate() - days);
      }
      
      console.log(`Time period ${period} converted to: ${startDate.toISOString()}`);
      return startDate.toISOString();
    } catch (error) {
      console.error("Error converting time period:", error);
      return undefined;
    }
  };

  // Use totalCount for pagination
  const totalCount = syncLogsData?.totalCount || 0;

  // Build columns using priority 1 fields
  const columns: ColumnDef<FieldMappingSyncLog>[] = [
    ...ALL_COLUMNS.filter(col => col.priority === 1).map(col => {
      // Custom cell renderers for some columns
      if (col.key === "partitionKey") {
        return {
          accessorKey: "partitionKey",
          header: "Partition Key",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const partitionKey = row.original.partitionKey;
            return (
              <div className="flex items-center space-x-2">
                <span className="font-medium">{partitionKey}</span>
              </div>
            );
          },
        };
      }
      if (col.key === "status") {
        return {
          accessorKey: "status",
          header: "Status",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const status = row.original.status;
            let color = "bg-gray-200 text-gray-800";
            if (status === "done") color = "bg-green-100 text-green-800";
            else if (status === "error") color = "bg-red-100 text-red-800";
            else if (status === "pending") color = "bg-blue-100 text-blue-800";
            else if (status === "no_update_needed") color = "bg-yellow-100 text-yellow-800";
            return <Badge className={color}>{status}</Badge>;
          },
        };
      }
      if (col.key === "timestamp") {
        return {
          accessorKey: "timestamp",
          header: "Timestamp",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const timestamp = row.original.timestamp || row.original.processedAt;
            return (
              <div className="text-sm">
                {timestamp instanceof Date
                  ? format(timestamp, "yyyy-MM-dd HH:mm:ss")
                  : timestamp
                    ? format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")
                    : "N/A"}
              </div>
            );
          },
        };
      }
      if (col.key === "updatedFieldsCount") {
        return {
          accessorKey: "updatedFields",
          header: "# Updated",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const updatedFields = row.original.updatedFields;

            if (!updatedFields) {
              return <span className="text-gray-400">0</span>;
            }

            // Parse updatedFields if it's a string
            let fieldsArray: any[] = [];
            try {
              if (typeof updatedFields === 'string') {
                fieldsArray = JSON.parse(updatedFields);
              } else if (Array.isArray(updatedFields)) {
                fieldsArray = updatedFields;
              }
            } catch (_error) {
              return <span className="text-gray-400">-</span>;
            }

            const count = Array.isArray(fieldsArray) ? fieldsArray.length : 0;

            if (count === 0) {
              return <span className="text-gray-400">0</span>;
            }

            // Extract field names
            const fieldNames = fieldsArray.map((field: any) => {
              if (typeof field === 'string') return field;
              if (field?.fieldName) return field.fieldName;
              if (field?.name) return field.name;
              return JSON.stringify(field);
            });

            return (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="inline-block">
                      <Badge
                        variant="secondary"
                        className="cursor-help bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-full px-2.5 py-0.5 font-semibold"
                      >
                        {count}
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-lg p-3" side="right" align="start">
                    <div className="text-sm space-y-1">
                      {fieldNames.map((fieldName, idx) => (
                        <div key={idx} className="text-xs">• {fieldName}</div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          },
        };
      }
      if (col.key === "syncLogs") {
        return {
          accessorKey: "syncLogs",
          header: "Logs",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const logs = row.getValue("syncLogs");
            const logText = typeof logs === 'object' && logs !== null
              ? JSON.stringify(logs)
              : logs as string;
            return (
              <div className="max-w-[400px] overflow-hidden">
                <div className="line-clamp-2 text-sm whitespace-pre-wrap break-words">
                  {logText}
                </div>
              </div>
            );
          },
        };
      }
      // Default: generic cell
      return {
        accessorKey: col.key,
        header: col.label,
        cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
          const value = row.original[col.key as keyof FieldMappingSyncLog];
          if (value === undefined || value === null) return <span className="text-gray-400">N/A</span>;
          if (typeof value === "boolean") return value ? "Yes" : "No";
          if (typeof value === "object") return <span className="text-xs">{JSON.stringify(value)}</span>;
          return <span>{String(value)}</span>;
        },
      };
    }),
    // Always include actions column
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
        const log = row.original;
        return (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedLog(row.original);
                setIsDetailsOpen(true);
              }}
            >
              Details
            </Button>
          </div>
        );
      },
    },
  ];

  // Update table instance for server-side pagination
  const table = useReactTable<FieldMappingSyncLog>({
    data: syncLogsData?.logs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: currentCursorIndex,
        pageSize: limit,
      },
    },
    manualPagination: true,
    pageCount: Math.ceil(totalCount / limit),
  });

  const handleSearch = () => {
    if (searchInputRef.current) {
      const term = searchInputRef.current.value.trim();
      
      // Only update if the term has changed
      if (term !== searchTerm) {
        console.log(`Searching for: "${term}" (server-side search across all fields)`);
        setSearchTerm(term);
        
        // Reset cursor stack when changing search term - effect will handle refetch
        setCursorStack([{ lastSeenTimestamp: null, lastSeenId: null }]);
        setCurrentCursorIndex(0);
      }
    }
  };

  const clearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    
    // Only update if there was a search term
    if (searchTerm) {
      console.log("Clearing search term");
      setSearchTerm("");
      
      // Reset cursor stack when clearing search term - effect will handle refetch
      setCursorStack([{ lastSeenTimestamp: null, lastSeenId: null }]);
      setCurrentCursorIndex(0);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(handleRefresh, isRefetching || syncLogsLoading);
    }
  }, [onRefreshReady, isRefetching, syncLogsLoading]);

  const clearFilters = () => {
    setParentEntity("all");
    setStatusFilter({ mode: 'include', statuses: [] });
    setBatchId("all");
    setTimePeriod("all");
  };

  // Create status filter options
  const statusOptions = [
    { value: "done", label: "Done" },
    { value: "error", label: "Error" },
    { value: "pending", label: "Pending" },
    { value: "no_update_needed", label: "No Updates" },
  ];

  // Create time period options
  const timePeriodOptions = [
    { value: "all", label: "All Time" },
    { value: "1h", label: "Last Hour" },
    { value: "4h", label: "Last 4 Hours" },
    { value: "1d", label: "Last 24 Hours" },
    { value: "2d", label: "Last 2 Days" },
    { value: "3d", label: "Last 3 Days" },
    { value: "4d", label: "Last 4 Days" },
    { value: "5d", label: "Last 5 Days" },
    { value: "6d", label: "Last 6 Days" },
    { value: "7d", label: "Last 7 Days" },
  ];

  // When filters or limit change, reset cursor stack
  useEffect(() => {
    setCursorStack([{ lastSeenTimestamp: null, lastSeenId: null }]);
    setCurrentCursorIndex(0);
  }, [parentEntity, statusFilter, batchId, timePeriod, limit, searchTerm]);

  // Pagination handlers
  const handleNextPage = () => {
    if (syncLogsData && Array.isArray(syncLogsData.logs) && syncLogsData.logs.length > 0) {
      const lastLog = syncLogsData.logs[syncLogsData.logs.length - 1];
      
      // Get timestamp with fallback to processedAt
      let lastSeenTimestamp = null;
      if (lastLog.timestamp) {
        lastSeenTimestamp = typeof lastLog.timestamp === 'string' 
          ? lastLog.timestamp 
          : new Date(lastLog.timestamp).toISOString();
      } else if (lastLog.processedAt) {
        lastSeenTimestamp = typeof lastLog.processedAt === 'string' 
          ? lastLog.processedAt 
          : new Date(lastLog.processedAt).toISOString();
      }
      
      // Key insight: The backend KQL query specifically uses:
      // customDimensions.mappingId for cursor-based pagination
      // So we need a reliable value for mappingId
      let lastSeenId = null;
      
      // Primary option: Use actual mappingId if available
      if (lastLog.mappingId) {
        lastSeenId = lastLog.mappingId;
      } 
      // Create a composite ID from batchId, entityId, and timestamp
      // This provides more uniqueness than just using one field
      else {
        const components = [];
        
        // Add batchId if available
        if (lastLog.batchId) {
          components.push(`batch_${lastLog.batchId}`);
        }
        
        // Add entityId if available
        if (lastLog.entityId) {
          components.push(`entity_${lastLog.entityId}`);
        }
        
        // Add entityName if available
        if (lastLog.entityName) {
          components.push(`ent_${lastLog.entityName}`);
        }
        
        // Add a timestamp component (always available)
        const timeComponent = lastSeenTimestamp 
          ? `t_${lastSeenTimestamp.replace(/[^0-9]/g, '')}`
          : `t_${Date.now()}`;
        components.push(timeComponent);
        
        // Create a composite ID with all available components
        // Ensuring it's properly sortable by having consistent components
        lastSeenId = components.join('_');
        
        // Ensure ID is unique by adding a random suffix if necessary
        if (components.length < 3) {
          lastSeenId += `_r${Math.random().toString(36).substring(2, 7)}`;
        }
      }
      
      // Ensure we have a timestamp for the cursor
      if (lastSeenTimestamp) {
        console.log("Setting cursor for next page:", { lastSeenTimestamp, lastSeenId });
        setCursorStack((prev) => [
          ...prev.slice(0, currentCursorIndex + 1),
          { lastSeenTimestamp, lastSeenId },
        ]);
        setCurrentCursorIndex((idx) => idx + 1);
      } else {
        console.error("Failed to extract timestamp from last log:", lastLog);
      }
    }
  };
  const handlePrevPage = () => {
    if (currentCursorIndex > 0) {
      setCurrentCursorIndex((idx) => idx - 1);
    }
  };

  // Defensive: Only allow valid entity names (not query strings)
  const handleParentEntityChange = (value: string) => {
    // Defensive: Only allow valid entity names (not query strings)
    if (typeof value === 'string' && !value.includes('=') && !value.includes('&')) {
      setParentEntity(value);
    } else if (value === 'all') {
      setParentEntity('all');
    }
  };

  return (
    <ViewGuard section="fieldMappings" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view Field Sync Logs</p>
        </div>
      </div>
    }>
    <PageWrapper withBackground padding="small">
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium">
            Field Mapping Logs {syncLogsData?.totalCount !== undefined ? `(${syncLogsData.totalCount})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Input
                  ref={searchInputRef}
                  placeholder="Search logs..."
                  className="h-8 w-64"
                  defaultValue={searchTerm}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
                <Button size="sm" variant="secondary" onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSearch}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                {/* Filter Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                      {(parentEntity !== "all" || statusFilter.statuses.length > 0 || batchId !== "all" || timePeriod !== "all") && (
                        <Badge variant="secondary" className="ml-2 px-1.5 py-0.5 text-xs">
                          {[parentEntity !== "all", statusFilter.statuses.length > 0, batchId !== "all", timePeriod !== "all"].filter(Boolean).length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Filters</h4>
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          Clear All
                        </Button>
                      </div>

                      <div className="space-y-3">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground">Parent Entity</label>
                          <Select value={parentEntity} onValueChange={handleParentEntityChange}>
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Select Parent Entity" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Parent Entities</SelectItem>
                              {entitiesLoading ? (
                                <SelectItem value="loading" disabled>
                                  Loading...
                                </SelectItem>
                              ) : (
                                uniqueParentEntities.map((entity: string) => (
                                  <SelectItem key={entity} value={entity}>
                                    {entity}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground">Status</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="h-8 w-full justify-between font-normal"
                              >
                                <span className="truncate">
                                  {statusFilter.statuses.length === 0
                                    ? "All Statuses"
                                    : `${statusFilter.statuses.length} selected`}
                                </span>
                                <Filter className="h-4 w-4 ml-2 shrink-0" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-0" align="start">
                              <div className="p-3 space-y-3">
                                {/* Include/Exclude Toggle */}
                                <div className="flex items-center gap-2">
                                  <label className="text-xs font-medium text-muted-foreground flex-1">
                                    Filter Mode
                                  </label>
                                  <Select
                                    value={statusFilter.mode}
                                    onValueChange={(mode: 'include' | 'exclude') =>
                                      setStatusFilter({ ...statusFilter, mode })
                                    }
                                  >
                                    <SelectTrigger className="h-7 w-28">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="include">Include</SelectItem>
                                      <SelectItem value="exclude">Exclude</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                <Separator />

                                {/* Status Checkboxes */}
                                <div className="space-y-2">
                                  {statusOptions.map((option) => (
                                    <div key={option.value} className="flex items-center gap-2">
                                      <Checkbox
                                        id={`status-${option.value}`}
                                        checked={statusFilter.statuses.includes(option.value)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setStatusFilter({
                                              ...statusFilter,
                                              statuses: [...statusFilter.statuses, option.value]
                                            });
                                          } else {
                                            setStatusFilter({
                                              ...statusFilter,
                                              statuses: statusFilter.statuses.filter(
                                                (s) => s !== option.value
                                              )
                                            });
                                          }
                                        }}
                                      />
                                      <label
                                        htmlFor={`status-${option.value}`}
                                        className="text-sm cursor-pointer flex-1"
                                      >
                                        {option.label}
                                      </label>
                                    </div>
                                  ))}
                                </div>

                                <Separator />

                                {/* Actions */}
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 h-7 text-xs"
                                    onClick={() =>
                                      setStatusFilter({
                                        ...statusFilter,
                                        statuses: statusOptions.map((o) => o.value)
                                      })
                                    }
                                  >
                                    Select All
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 h-7 text-xs"
                                    onClick={() =>
                                      setStatusFilter({ ...statusFilter, statuses: [] })
                                    }
                                  >
                                    Clear
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground">
                            Batches 
                          </label>
                          <Select value={batchId} onValueChange={setBatchId}>
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Select Batch ID" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">
                                All Batch IDs
                                {batchIdsData?.metadata?.totalRecords !== undefined && (
                                  batchIdsData.metadata.totalRecords === 0
                                    ? <span className="text-red-600 font-semibold"> ✗</span>
                                    : ` (${batchIdsData.metadata.totalRecords.toLocaleString()})`
                                )}
                              </SelectItem>
                              {batchIdsWithCounts.length === 0 ? (
                                <SelectItem value="loading" disabled>
                                  <span className="text-red-600 font-medium">✗ No batches found</span>
                                </SelectItem>
                              ) : (
                                batchIdsWithCounts.map((batch) => (
                                  <SelectItem key={batch.batchId} value={batch.batchId}>
                                    {batch.batchId} ({batch.recordCount.toLocaleString()})
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground">Time Period</label>
                          <Select value={timePeriod} onValueChange={setTimePeriod}>
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Select Time Period" />
                            </SelectTrigger>
                            <SelectContent>
                              {timePeriodOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {syncLogsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-20 w-full" />
                ))}
              </div>
            ) : table.getRowModel().rows?.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No logs found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {table.getRowModel().rows.map((row) => {
                  const log = row.original;
                  const logId = `${log.entityId}-${log.timestamp}`;
                  const isExpanded = expandedCards.has(logId);

                  // Determine status styling
                  const getStatusStyling = () => {
                    const status = log.status?.toString().toLowerCase();
                    if (status === "done" || status === "success") {
                      return {
                        borderClass: 'border-l-green-500',
                        bgClass: 'bg-green-50/20',
                        hoverBorder: 'hover:border-green-400',
                        badgeClass: 'bg-green-100 text-green-800'
                      };
                    } else if (status === "error" || status === "failure" || status?.includes("error")) {
                      return {
                        borderClass: 'border-l-red-500',
                        bgClass: 'bg-red-50/20',
                        hoverBorder: 'hover:border-red-400',
                        badgeClass: 'bg-red-100 text-red-800'
                      };
                    } else if (status === "pending") {
                      return {
                        borderClass: 'border-l-blue-500',
                        bgClass: 'bg-blue-50/20',
                        hoverBorder: 'hover:border-blue-400',
                        badgeClass: 'bg-blue-100 text-blue-800'
                      };
                    } else if (status === "no_update_needed") {
                      return {
                        borderClass: 'border-l-yellow-500',
                        bgClass: 'bg-yellow-50/20',
                        hoverBorder: 'hover:border-yellow-400',
                        badgeClass: 'bg-yellow-100 text-yellow-800'
                      };
                    } else {
                      return {
                        borderClass: 'border-l-gray-500',
                        bgClass: 'bg-gray-50/20',
                        hoverBorder: 'hover:border-gray-400',
                        badgeClass: 'bg-gray-100 text-gray-800'
                      };
                    }
                  };

                  const styling = getStatusStyling();
                  const timestamp = log.timestamp ? new Date(log.timestamp) : null;
                  const formattedTime = timestamp ? format(timestamp, "MMM d, HH:mm:ss") : "N/A";

                  // Parse updated fields
                  let updatedFieldsList: string[] = [];
                  try {
                    if (log.updatedFields) {
                      updatedFieldsList = typeof log.updatedFields === 'string'
                        ? JSON.parse(log.updatedFields)
                        : log.updatedFields;
                    }
                  } catch (_error) {
                    // If parsing fails, just use the string as-is
                    updatedFieldsList = log.updatedFields ? [log.updatedFields] : [];
                  }

                  const _fieldsText = updatedFieldsList.length > 0
                    ? updatedFieldsList.slice(0, 2).join(", ") + (updatedFieldsList.length > 2 ? "..." : "")
                    : "No fields updated";

                  return (
                    <div
                      key={logId}
                      className={`border rounded-lg border-l-[3px] transition-all ${styling.borderClass} ${styling.bgClass} ${styling.hoverBorder}`}
                    >
                      {/* Collapsed view - 2 lines */}
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="font-semibold text-sm truncate">
                              {log.entityName}
                            </span>
                            <Badge className={`${styling.badgeClass} text-xs shrink-0`}>
                              {log.status}
                            </Badge>
                            {updatedFieldsList.length > 0 && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {updatedFieldsList.length} {updatedFieldsList.length === 1 ? 'field' : 'fields'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground">{formattedTime}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                const newExpanded = new Set(expandedCards);
                                if (isExpanded) {
                                  newExpanded.delete(logId);
                                } else {
                                  newExpanded.add(logId);
                                }
                                setExpandedCards(newExpanded);
                              }}
                            >
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-muted-foreground pl-6">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="truncate">ID: <span className="font-mono">{log.entityId}</span></span>
                            <span>•</span>
                            <span className="truncate">Batch: <span className="font-mono">{log.batchId?.substring(0, 13)}...</span></span>
                          </div>
                        </div>
                      </div>

                      {/* Expanded view */}
                      {isExpanded && (
                        <div className="border-t px-3 py-3 space-y-3">
                          {/* Entity Info */}
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-muted-foreground">Entity Details</div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground">Entity Name:</span>
                                <div className="font-medium">{log.entityName}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Entity ID:</span>
                                <div className="font-mono truncate">{log.entityId}</div>
                              </div>
                            </div>
                          </div>

                          {/* Updated Fields */}
                          {updatedFieldsList.length > 0 && (
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-muted-foreground">
                                Updated Fields ({updatedFieldsList.length})
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {updatedFieldsList.map((field, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Metadata */}
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-muted-foreground">Metadata</div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground">Batch ID:</span>
                                <div className="font-mono truncate">{log.batchId}</div>
                              </div>
                              {log.mappingId && (
                                <div>
                                  <span className="text-muted-foreground">Mapping ID:</span>
                                  <div className="font-mono truncate">{log.mappingId}</div>
                                </div>
                              )}
                              {log.correlationId && (
                                <div>
                                  <span className="text-muted-foreground">Correlation ID:</span>
                                  <div className="font-mono truncate">{log.correlationId}</div>
                                </div>
                              )}
                              {log.processedAt && (
                                <div>
                                  <span className="text-muted-foreground">Processed At:</span>
                                  <div>{format(new Date(log.processedAt), "MMM d, yyyy HH:mm:ss")}</div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Error or Message */}
                          {(log.error || log.errorMessage || log.message) && (
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-muted-foreground">
                                {log.error || log.errorMessage ? "Error" : "Message"}
                              </div>
                              <div className={`text-xs p-2 rounded ${(log.error || log.errorMessage) ? 'bg-red-50 text-red-900' : 'bg-blue-50 text-blue-900'}`}>
                                {log.error || log.errorMessage || log.message}
                              </div>
                            </div>
                          )}

                          {/* Sync Logs */}
                          {log.syncLogs && (
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-muted-foreground">Sync Logs</div>
                              <div className="text-xs p-2 rounded bg-gray-50 text-gray-900 font-mono max-h-40 overflow-y-auto">
                                {log.syncLogs}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => {
                                navigator.clipboard.writeText(log.batchId || '');
                              }}
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy Batch ID
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => {
                                navigator.clipboard.writeText(log.entityId || '');
                              }}
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy Entity ID
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {syncLogsData?.logs.length} row(s) on this page of {syncLogsData?.totalCount || 0} total logs
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium">Rows per page</p>
                  <Select
                    value={limit.toString()}
                    onValueChange={(value) => setLimit(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={limit.toString()} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {[10, 25, 50, 100].map((pageSize) => (
                        <SelectItem key={pageSize} value={pageSize.toString()}>
                          {pageSize}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentCursorIndex === 0}
                  >
                    <span className="sr-only">Go to previous page</span>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    Page {currentCursorIndex + 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!syncLogsData?.logs || syncLogsData.logs.length < limit}
                  >
                    <span className="sr-only">Go to next page</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-2">
          <DialogHeader className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Log Details</DialogTitle>
                <DialogDescription>
                  Details for log entry {selectedLog?.partitionKey}
                </DialogDescription>
              </div>
              {selectedLog && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={!selectedLog.entityId || !selectedLog.entityName}
                        className="mr-10"
                        onClick={() => {
                          if (selectedLog.entityId && selectedLog.entityName) {
                            const recordUrl = `${import.meta.env.VITE_DATAVERSE_URL}/main.aspx?appid=${import.meta.env.VITE_DATAVERSE_APP_ID}&pagetype=entityrecord&etn=${selectedLog.entityName}&id=${selectedLog.entityId}`;
                            window.open(recordUrl, "_blank");
                          }
                        }}
                      >
                        <FaMicrosoft className="h-4 w-4 mr-2" />
                        View in Dataverse
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View this record in Dataverse</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 max-w-full">
              <div className="space-y-4">
                <Card className="w-full max-w-full">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm">Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2">
                    <div className="grid grid-cols-[auto,1fr] gap-4">
                      {/* Dynamically display all available properties */}
                      {Object.entries(selectedLog).map(([key, value]) => {
                        // Skip rendering syncLogs here as it has its own section
                        if (key === 'syncLogs') return null;
                        
                        // Format the value based on its type
                        let displayValue: React.ReactNode = "N/A";
                        
                        if (value !== null && value !== undefined) {
                          if (key === "status") {
                            const statusColor = 
                              value === "done"
                                ? "bg-green-100 text-green-800"
                                : value === "error"
                                  ? "bg-red-100 text-red-800"
                                  : value === "pending"
                                    ? "bg-blue-100 text-blue-800"
                                    : value === "no_update_needed"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-200 text-gray-800";
                            displayValue = <Badge className={statusColor}>{value}</Badge>;
                          } else if (key === "timestamp" || key === "processedAt") {
                            displayValue = value instanceof Date
                              ? format(value, "yyyy-MM-dd HH:mm:ss")
                              : typeof value === 'string'
                                ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
                                : String(value);
                          } else if (key === "updatedFields" && typeof value === "string") {
                            // Special handling for updatedFields - try to parse and format nicely
                            try {
                              const parsed = JSON.parse(value);
                              if (Array.isArray(parsed)) {
                                displayValue = (
                                  <div className="space-y-1">
                                    {parsed.map((field: any, index: number) => (
                                      <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                                        {typeof field === 'object' ? (
                                          Object.entries(field).map(([k, v]) => (
                                            <div key={k}><span className="font-medium">{k}:</span> {String(v)}</div>
                                          ))
                                        ) : (
                                          String(field)
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                );
                              } else {
                                displayValue = (
                                  <pre className="text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap">
                                    {JSON.stringify(parsed, null, 2)}
                                  </pre>
                                );
                              }
                            } catch {
                              // If not valid JSON, display as string
                              displayValue = <span className="text-xs whitespace-pre-wrap">{value}</span>;
                            }
                          } else if (typeof value === "object") {
                            displayValue = <span className="text-xs">{JSON.stringify(value)}</span>;
                          } else {
                            displayValue = String(value);
                          }
                        }
                        
                        // Format the label
                        const formattedKey = key.replace(/([A-Z])/g, ' $1')
                          .replace(/^./, str => str.toUpperCase());
                        
                        return (
                          <React.Fragment key={key}>
                            <span className="text-sm font-medium">{formattedKey}:</span>
                            <span className="text-sm break-words" title={typeof displayValue === 'string' ? displayValue : ''}>
                              {displayValue}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Sync Logs Section */}
                <Card className="w-full max-w-full">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm">Sync Logs</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="w-full overflow-hidden">
                      {(() => {
                        try {
                          // Handle when syncLogs is already an object (array)
                          if (typeof selectedLog.syncLogs === 'object') {
                            const logsArray = Array.isArray(selectedLog.syncLogs) 
                              ? selectedLog.syncLogs 
                              : [selectedLog.syncLogs];
                              
                            return (
                              <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                                {logsArray.map((item: any, index: number) => (
                                  <div key={index} className="mb-2 border-b pb-2">
                                    {Object.entries(item).map(([key, value], subIndex) => (
                                      <div key={`${index}-${subIndex}`} className="mb-1 ml-2">
                                        <span className="font-semibold text-blue-600">
                                          {key}:{" "}
                                        </span>
                                        <span className="whitespace-pre-wrap break-words">
                                          {typeof value === "object" && value !== null
                                            ? JSON.stringify(value, null, 2)
                                            : value === null ? "null" : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          
                          // Try to parse as JSON if it's a string
                          const logContent = selectedLog.syncLogs;
                          
                          // Check if it looks like JSON
                          if (
                            typeof logContent === 'string' &&
                            (logContent.trim().startsWith("{") || logContent.trim().startsWith("["))
                          ) {
                            try {
                              const parsedJson = JSON.parse(logContent);
                              return (
                                <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                                  {Object.entries(parsedJson).map(
                                    ([key, value], index) => (
                                      <div key={index} className="mb-1">
                                        <span className="font-semibold text-blue-600">
                                          {key}:{" "}
                                        </span>
                                        <span className="whitespace-pre-wrap break-words">
                                          {typeof value === "object" && value !== null
                                            ? JSON.stringify(value, null, 2)
                                            : value === null ? "null" : String(value)}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              );
                            } catch (_error) {
                              // If JSON parsing fails, check for structured log format
                              if (logContent.includes("\n")) {
                                // Handle line-by-line logs with timestamps or key-value pairs
                                const lines = logContent
                                  .split("\n")
                                  .filter((line: string) => line.trim());
                                return (
                                  <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                                    {lines.map((line: string, index: number) => {
                                      // Try to identify if line has timestamp or key:value format
                                      const timestampMatch = line.match(
                                        /^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})/,
                                      );
                                      const keyValueMatch =
                                        line.match(/^([^:]+):(.*)/);

                                      if (timestampMatch) {
                                        const [fullMatch, timestamp] =
                                          timestampMatch;
                                        const message = line.substring(
                                          fullMatch.length,
                                        );
                                        return (
                                          <div key={index} className="mb-1">
                                            <span className="font-semibold text-blue-600">
                                              {timestamp}
                                            </span>
                                            <span className="whitespace-pre-wrap break-words">
                                              {message}
                                            </span>
                                          </div>
                                        );
                                      } else if (keyValueMatch) {
                                        const key = keyValueMatch[1];
                                        const value = keyValueMatch[2];
                                        return (
                                          <div key={index} className="mb-1">
                                            <span className="font-semibold text-blue-600">
                                              {key}:
                                            </span>
                                            <span className="whitespace-pre-wrap break-words">
                                              {value}
                                            </span>
                                          </div>
                                        );
                                      }
                                      return (
                                        <div key={index} className="mb-1">
                                          <span className="whitespace-pre-wrap break-words text-gray-800">
                                            {line}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              }
                            }
                          }

                          // Fallback: plain text display
                          return (
                            <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                              <pre className="whitespace-pre-wrap break-words">
                                {String(selectedLog.syncLogs)}
                              </pre>
                            </div>
                          );
                        } catch (_error) {
                          return (
                            <div className="text-red-500 text-sm">
                              Error rendering sync logs.
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

        </DialogContent>
      </Dialog>
    </PageWrapper>
    </ViewGuard>
  );
};

export default FieldSyncLogs;
